import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { COMMON_API } from '../../CommonAPI';

const UpdateProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    category: [],
    name: '',
    desktopImages: [], // Separate arrays for clarity
    mobileImages: []
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const [projectRes, categoriesRes] = await Promise.all([
          axios.get(`${COMMON_API}/api/project/single-project/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${COMMON_API}/api/project/categories`)
        ]);

        const project = projectRes.data;
        setCategories(categoriesRes.data.categories || []);

        // Split existing images
        const desktop = [];
        const mobile = [];
        project.images.forEach(img => {
          if (img.desk_img) desktop.push({ id: img.id, desk_img: img.desk_img, newDeskImg: null });
          if (img.mob_img) mobile.push({ id: img.id, mob_img: img.mob_img, newMobImg: null });
        });

        setForm({
          category: project.categories,
          name: project.title || '',
          desktopImages: desktop,
          mobileImages: mobile
        });
      } catch (err) {
        console.error(err);
        toast.error('Error loading project data.');
      }
    };
    fetchData();
  }, [id]);

  const handleChange = e => {
    const { name, value, checked } = e.target;
    if (name === 'category') {
      setForm(prev => ({
        ...prev,
        category: checked ? [...prev.category, value] : prev.category.filter(cat => cat !== value)
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDesktopChange = (index, file) => {
    setForm(prev => {
      const updated = [...prev.desktopImages];
      updated[index].newDeskImg = file;
      return { ...prev, desktopImages: updated };
    });
  };

  const handleMobileChange = (index, file) => {
    setForm(prev => {
      const updated = [...prev.mobileImages];
      updated[index].newMobImg = file;
      return { ...prev, mobileImages: updated };
    });
  };

  const addDesktopImage = () => {
    setForm(prev => ({
      ...prev,
      desktopImages: [...prev.desktopImages, { id: null, desk_img: null, newDeskImg: null }]
    }));
  };

  const addMobileImage = () => {
    setForm(prev => ({
      ...prev,
      mobileImages: [...prev.mobileImages, { id: null, mob_img: null, newMobImg: null }]
    }));
  };

  // const removeDesktopImage = index => {
  //   setForm(prev => {
  //     const updated = [...prev.desktopImages];
  //     updated.splice(index, 1);
  //     return { ...prev, desktopImages: updated };
  //   });
  // };

  // const removeMobileImage = index => {
  //   setForm(prev => {
  //     const updated = [...prev.mobileImages];
  //     updated.splice(index, 1);
  //     return { ...prev, mobileImages: updated };
  //   });
  // };


  const removeDesktopImage = async (index) => {
    const image = form.desktopImages[index];
    if (image.id) {
      const confirmDelete = window.confirm("Are you sure you want to delete this project?");
        if (!confirmDelete) return;
      try {
        const token = sessionStorage.getItem('authToken');
        await axios.delete(`${COMMON_API}/api/project/delete-image/${image.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Desktop image deleted!');
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete desktop image!');
        return;
      }
    }
    setForm(prev => {
      const updated = [...prev.desktopImages];
      updated.splice(index, 1);
      return { ...prev, desktopImages: updated };
    });
  };
  
  const removeMobileImage = async (index) => {
    const image = form.mobileImages[index];
    if (image.id) {
      const confirmDelete = window.confirm("Are you sure you want to delete this project?");
        if (!confirmDelete) return;
      try {
        const token = sessionStorage.getItem('authToken');
        await axios.delete(`${COMMON_API}/api/project/delete-image/${image.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Mobile image deleted!');
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete mobile image!');
        return;
      }
    }
    setForm(prev => {
      const updated = [...prev.mobileImages];
      updated.splice(index, 1);
      return { ...prev, mobileImages: updated };
    });
  };

  
  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('authToken');
      const formData = new FormData();

      categories
        .filter(cat => form.category.includes(cat.category))
        .map(cat => cat.id)
        .forEach(id => formData.append('category', id));

      formData.append('name', form.name);

      form.desktopImages.forEach(img => {
        if (img.id) {
          formData.append('image_ids[]', img.id);
          if (img.newDeskImg) formData.append(`desk_img_${img.id}`, img.newDeskImg);
        } else if (img.newDeskImg) {
          formData.append('image', img.newDeskImg);
        }
      });

      form.mobileImages.forEach(img => {
        if (img.id) {
          formData.append('image_ids[]', img.id);
          if (img.newMobImg) formData.append(`mob_img_${img.id}`, img.newMobImg);
        } else if (img.newMobImg) {
          formData.append('big_image', img.newMobImg);
        }
      });

      await axios.put(`${COMMON_API}/api/project/update-project/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Project updated successfully!');
      navigate('/projects');

    } catch (error) {
      console.error(error);
      toast.error('Failed to update project!');
    }
  };

  return (
    <div className="ml-64 p-8 bg-gray-100 min-h-screen">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow space-y-4">
        <h2 className="text-2xl font-semibold mb-4">Update Project</h2>

        <div>
          <label className="block font-medium mb-1">Select Categories:</label>
          <div className="grid grid-cols-2 gap-2">
            {categories.map(cat => (
              <label key={cat.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="category"
                  value={cat.category}
                  checked={form.category.includes(cat.category)}
                  onChange={handleChange}
                  className="accent-blue-600"
                />
                <span>{cat.category}</span>
              </label>
            ))}
          </div>
        </div>

        <input
          type="text"
          name="name"
          placeholder="Project Name"
          value={form.name}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />

        <div>
          <h4 className="font-semibold">Desktop Images</h4>
          {form.desktopImages.map((img, index) => (
            <div key={index} className="border p-3 rounded bg-gray-50 mb-2 relative">
              {img.newDeskImg
                ? <img src={URL.createObjectURL(img.newDeskImg)} alt="Preview" className="w-32 h-20 object-cover rounded mb-2" />
                : img.desk_img && <img src={`${COMMON_API}/api/upload/${img.desk_img}`} alt="Existing" className="w-32 h-20 object-cover rounded mb-2" />
              }
              <input
                type="file"
                accept="image/*"
                onChange={e => handleDesktopChange(index, e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <button
                type="button"
                onClick={() => removeDesktopImage(index)}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                ✖
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addDesktopImage}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add Desktop Image
          </button>
        </div>

        <div>
          <h4 className="font-semibold">Mobile Images</h4>
          {form.mobileImages.map((img, index) => (
            <div key={index} className="border p-3 rounded bg-gray-50 mb-2 relative">
              {img.newMobImg
                ? <img src={URL.createObjectURL(img.newMobImg)} alt="Preview" className="w-32 h-20 object-cover rounded mb-2" />
                : img.mob_img && <img src={`${COMMON_API}/api/upload/${img.mob_img}`} alt="Existing" className="w-32 h-20 object-cover rounded mb-2" />
              }
              <input
                type="file"
                accept="image/*"
                onChange={e => handleMobileChange(index, e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <button
                type="button"
                onClick={() => removeMobileImage(index)}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                ✖
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addMobileImage}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add Mobile Image
          </button>
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 ml-3"
        >
          Update Project
        </button>
      </form>
    </div>
  );
};

export default UpdateProject;
